import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { getCategoryImage } from '@/lib/categoryImages';

const BE = process.env.REACT_APP_BACKEND_URL || '';

const heroSlides = [
  { id: 1, title: 'Bespoke Tailoring,\nCrafted for You', subtitle: 'Premium custom stitching with a perfect fit — measured, made, and delivered to your doorstep.', cta: 'Begin Your Story' },
  { id: 2, title: 'Your Design,\nOur Expertise', subtitle: 'Choose from our curated silhouettes or share your inspiration. Every detail, yours to compose.', cta: 'Explore Silhouettes' },
  { id: 3, title: 'Delivered with\nPerfection', subtitle: 'Master craftsmanship, delivered pressed and ready for its moment.', cta: 'Place an Order' },
  { id: 4, title: 'Style that\nFits Your Story', subtitle: 'A signature look, tailored just for you.', cta: 'Arrange a Callback' },
];

const process3 = [
  { n: '01', title: 'Design', desc: 'Choose from our curated silhouettes or upload your own inspiration. Every detail — neckline, sleeve, fall — is yours to compose.' },
  { n: '02', title: 'Measure', desc: 'Our stylist visits at your convenience for a precise measurement session. No queues, no compromises — a private consultation.' },
  { n: '03', title: 'Deliver', desc: 'Master tailors craft your piece over four to six weeks. Delivered pressed, wrapped, and ready for its moment.' },
];

const testimonials = [
  { text: 'The blouse fit as though it had been sculpted onto me. I\'ve never worn something that felt so intimately mine.', name: 'Priya Sharma', rating: 5 },
  { text: 'The team understood my vision perfectly. Every stitch, every detail — exquisite.', name: 'Anita Menon', rating: 5 },
  { text: 'Quick delivery, unmatched quality. Besq is now my go-to for every occasion.', name: 'Kavitha Reddy', rating: 5 },
];

const tileTag = (slug) => ({
  lehenga: 'Bridal · Couture', blouse: 'Fitted', kurti: 'Everyday', suit: 'Occasion',
  'ready-to-wear-saree': 'Pre-stitched', 'co-ord-set': 'Contemporary', dress: 'Resort',
  top: 'Everyday', skirt: 'Draped', pant: 'Tailored', others: 'Bespoke',
}[slug] || 'Signature');

const HERO_INTERVAL = 6000;
const TESTIMONIAL_INTERVAL = 7000;

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const heroTimerRef = useRef(null);

  useEffect(() => {
    api.get('/categories').then((res) => setCategories(res.data.data || [])).catch(() => {});
  }, []);

  const startHeroTimer = () => {
    if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    heroTimerRef.current = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % heroSlides.length);
    }, HERO_INTERVAL);
  };

  useEffect(() => {
    startHeroTimer();
    return () => heroTimerRef.current && clearInterval(heroTimerRef.current);
  }, []);

  // manual interactions restart the timer for a full interval
  const goToSlide = (i) => { setCurrentSlide(i); startHeroTimer(); };
  const nextSlide = () => goToSlide((currentSlide + 1) % heroSlides.length);
  const prevSlide = () => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    const t = setInterval(() => setTestimonialIdx((p) => (p + 1) % testimonials.length), TESTIMONIAL_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const slide = heroSlides[currentSlide];

  return (
    <div data-testid="home-page" className="bg-background">
      {/* ============ HERO ============ */}
      <section className="relative h-screen w-full overflow-hidden bg-[#2b1e1a]" data-testid="hero-carousel">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-hidden={i !== currentSlide}
          >
            <picture>
              <source media="(max-width: 767px)" srcSet={`${BE}/api/static/hero-banners/Hero_Banner_${s.id}_Mobile.webp`} />
              <source media="(max-width: 1024px)" srcSet={`${BE}/api/static/hero-banners/Hero_Banner_${s.id}_Tablet.webp`} />
              <img src={`${BE}/api/static/hero-banners/Hero_Banner_${s.id}.webp`} alt="" className="absolute inset-0 w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} fetchPriority={i === 0 ? 'high' : 'low'} />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />
            <div className="absolute inset-0 md:hidden bg-gradient-to-b from-black/25 via-transparent to-black/55" />
          </div>
        ))}

        {/* Slide content */}
        <div className="absolute inset-0 flex items-center pointer-events-none">
          <div key={currentSlide} className="max-w-[1440px] mx-auto w-full px-6 lg:px-24 pointer-events-auto">
            <h1
              className="hero-in font-display text-white font-light leading-[1.02] tracking-[-0.015em] whitespace-pre-line"
              style={{ fontSize: 'clamp(44px, 8vw, 96px)', textShadow: '0 2px 24px rgba(0,0,0,0.25)' }}
              data-testid="hero-title"
            >
              {slide.title}
            </h1>
            <p
              className="hero-in mt-8 text-white font-light max-w-xl"
              style={{ fontSize: 'clamp(17px, 1.4vw, 20px)', lineHeight: 1.65, animationDelay: '0.15s', textShadow: '0 1px 12px rgba(0,0,0,0.35)', opacity: 0.95 }}
              data-testid="hero-subtitle"
            >
              {slide.subtitle}
            </p>
            <div className="hero-in mt-12" style={{ animationDelay: '0.3s' }}>
              <Link
                to="/categories"
                data-testid="hero-cta"
                className="inline-flex items-center gap-3 border border-white text-white hover:text-ink hover:bg-white uppercase tracking-luxe font-medium transition-all duration-300"
                style={{ fontSize: 14, padding: '18px 40px' }}
              >
                {slide.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Arrows */}
        <button onClick={prevSlide} className="absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors" data-testid="hero-prev" aria-label="Previous">
          <ChevronLeft className="w-6 h-6" strokeWidth={1.2} />
        </button>
        <button onClick={nextSlide} className="absolute right-4 lg:right-10 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors" data-testid="hero-next" aria-label="Next">
          <ChevronRight className="w-6 h-6" strokeWidth={1.2} />
        </button>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`block h-[1px] transition-all duration-500 ${i === currentSlide ? 'bg-white w-10 h-[2px]' : 'bg-white/45 w-6'}`}
            />
          ))}
        </div>
      </section>

      {/* ============ THE BESQ WAY ============ */}
      <section className="py-24 md:py-32 px-6 lg:px-24" data-testid="how-it-works">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-24">
            <div>
              <div className="eyebrow mb-6">The Besq Way</div>
              <h2 className="font-display font-light text-ink leading-[1.05] tracking-[-0.015em]" style={{ fontSize: 'clamp(36px, 5vw, 64px)' }}>
                Effortless bespoke,<br />in three chapters.
              </h2>
            </div>
            <p className="text-ink/75 max-w-md" style={{ fontSize: 17, lineHeight: 1.7 }}>
              From your vision to your wardrobe. A concierge process built on precision, patience, and craftsmanship.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-20">
            {process3.map((p, i) => (
              <div key={i} data-testid={`how-step-${i}`}>
                <div className="font-display font-light text-gold leading-none mb-8" style={{ fontSize: 72 }}>{p.n}</div>
                <div className="w-10 h-[1px] bg-ink mb-7" />
                <h3 className="font-display text-ink mb-4 tracking-[-0.01em]" style={{ fontSize: 28 }}>{p.title}</h3>
                <p className="text-ink/75" style={{ fontSize: 15.5, lineHeight: 1.75 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SIGNATURE SILHOUETTES — SYMMETRIC 4-COL ============ */}
      <section className="py-24 md:py-32 px-6 lg:px-24 bg-cream-2" data-testid="explore-categories">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
            <div>
              <div className="eyebrow mb-6">Signature Silhouettes</div>
              <h2 className="font-display font-light text-ink leading-[1.05] tracking-[-0.015em]" style={{ fontSize: 'clamp(36px, 5vw, 64px)' }}>
                A wardrobe worth<br />the wait.
              </h2>
            </div>
            <Link to="/categories" data-testid="view-all-categories" className="inline-flex items-center gap-2 uppercase tracking-luxe text-[13px] font-semibold text-ink border-b border-ink pb-1 hover:text-gold hover:border-gold transition-colors self-start">
              View All Categories <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* All tiles same aspect (3/4) — full image visible, no odd crops */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            {categories.slice(0, 8).map((cat) => {
              const src = getCategoryImage(cat);
              return (
                <Link key={cat.id} to={`/categories/${cat.slug}`} data-testid={`category-card-${cat.slug}`} className="group">
                  <div className="tile-zoom relative aspect-[3/4] bg-secondary overflow-hidden">
                    {src && <img src={src} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />}
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-ink leading-none" style={{ fontSize: 26 }}>{cat.name}</div>
                      {cat.starting_price ? (
                        <div className="mt-2 uppercase tracking-luxe text-[11px] text-ink-muted" data-testid={`home-cat-price-${cat.slug}`}>
                          Starting at <span className="text-ink font-semibold tabular-nums">₹{cat.starting_price}</span>
                        </div>
                      ) : null}
                    </div>
                    <ArrowRight className="w-4 h-4 text-ink-muted group-hover:text-gold group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIAL ============ */}
      <section className="py-32 md:py-40 px-6 lg:px-24 text-center" data-testid="testimonials">
        <div className="max-w-4xl mx-auto">
          <div className="text-gold tracking-[8px] text-[12px] mb-10">★ ★ ★ ★ ★</div>
          <div className="relative min-h-[240px]">
            {testimonials.map((t, i) => (
              <blockquote
                key={i}
                data-testid={`testimonial-${i}`}
                className={`absolute inset-x-0 transition-opacity duration-1000 ${i === testimonialIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <p className="font-display font-light italic text-ink leading-[1.3] tracking-[-0.01em]" style={{ fontSize: 'clamp(20px, 2.2vw, 30px)' }}>
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="mt-10">
                  <div className="font-display text-ink" style={{ fontSize: 24 }}>{t.name}</div>
                  <div className="uppercase tracking-luxe text-[11px] text-ink-muted mt-2">&nbsp;</div>
                </div>
              </blockquote>
            ))}
          </div>
          <div className="flex justify-center gap-3 mt-16">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setTestimonialIdx(i)}
                aria-label={`Testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`block h-[1px] transition-all duration-500 ${i === testimonialIdx ? 'bg-ink w-10 h-[2px]' : 'bg-ink-muted/50 w-6'}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
